import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import AnimatedHeading from "../../animations/AnimatedHeading";
import WavyText from "../../animations/WavyText";
import Noise from "../../animations/Noise";

const BlogCTA = () => {
  const navigate = useNavigate();

  return (
    <section
      className="
        relative
        overflow-hidden
        theme-bg-primary
        py-6
        font-saira
        sm:py-10
        lg:py-14
      "
    >
      {/* Noise */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <Noise
          patternSize={250}
          patternScaleX={2}
          patternScaleY={2}
          patternRefreshInterval={2}
          patternAlpha={15}
        />
      </div>

      {/* Brand Glow */}
      <div
        className="
          absolute
          left-1/2
          top-1/2
          h-[320px]
          w-[320px]
          -translate-x-1/2
          -translate-y-1/2
          rounded-full
          blur-[120px]
          sm:h-[420px]
          sm:w-[420px]
        "
        style={{
          backgroundColor: "color-mix(in srgb, var(--brand-primary) 10%, transparent)",
        }}
      />

      {/* Grid */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `
            linear-gradient(
              to right,
              var(--text-primary) 1px,
              transparent 1px
            ),
            linear-gradient(
              to bottom,
              var(--text-primary) 1px,
              transparent 1px
            )
          `,
          backgroundSize: "70px 70px",
        }}
      />

      <div
        className="
          relative
          z-10
          mx-auto
          max-w-7xl
          px-5
          sm:px-6
          lg:px-8
          xl:px-10
        "
      >
        <motion.div
          initial={{ opacity: 0, y: 45 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.65 }}
          className="
            theme-card
            relative
            overflow-hidden
            rounded-[24px]
            border
            theme-border
            lg:rounded-[30px]
          "
        >
          {/* Top Accent */}
          <div
            className="h-[3px] w-full"
            style={{
              background: `
                linear-gradient(
                  to right,
                  var(--brand-primary),
                  var(--brand-light),
                  var(--brand-primary)
                )
              `,
            }}
          />

          {/* Glow */}
          <div
            className="
              absolute
              -right-16
              -top-16
              h-52
              w-52
              rounded-full
              blur-[110px]
            "
            style={{
              backgroundColor:
                "color-mix(in srgb, var(--brand-primary) 10%, transparent)",
            }}
          />

          <div
            className="
              relative
              z-10
              px-6
              py-10
              sm:px-10
              sm:py-12
              lg:px-16
              lg:py-14
              xl:px-20
              xl:py-16
            "
          >
            <div className="mx-auto max-w-4xl text-center">
              {/* Label */}
              <span
                className="
                  brand-color
                  text-[11px]
                  font-bold
                  uppercase
                  tracking-[0.35em]
                  sm:text-xs
                "
              >
                LET'S BUILD SOMETHING AMAZING
              </span>

              {/* Heading */}
              <AnimatedHeading
                as="h2"
                parts={[
                  "Transform Your Ideas",
                  "Into Powerful Digital Solutions",
                ]}
                className="
                  theme-text-primary
                  mt-5
                  text-3xl
                  font-bold
                  leading-tight
                  sm:text-4xl
                  lg:text-5xl
                  xl:text-6xl
                "
              />

              {/* Description */}
              <p
                className="
                  theme-text-muted
                  mx-auto
                  mt-6
                  max-w-3xl
                  text-sm
                  leading-7
                  sm:text-base
                  sm:leading-8
                  lg:text-lg
                "
              >
                Whether you're planning a custom software solution,
                enterprise web application, AI-powered platform, or business
                automation system, our team is ready to build scalable
                digital products that accelerate your growth.
              </p>

              {/* CTA Button */}
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => {
                  navigate("/contact#contact-form");
                }}
                className="
                  group
                  relative
                  mt-8
                  inline-flex
                  overflow-hidden
                  rounded-full
                  brand-bg
                  px-6
                  py-3.5
                  font-semibold
                  text-white
                  transition-all
                  duration-300
                  sm:mt-10
                  sm:px-8
                  sm:py-4
                  lg:px-10
                  hover:text-white
                "
              >
                {/* Button Hover Layer */}
                <span
                  className="
                    absolute
                    inset-0
                    w-0
                    transition-all
                    duration-500
                    group-hover:w-full
                  "
                  style={{
                    backgroundColor: "var(--bg-primary)",
                  }}
                />

                <span
                  className="
                    relative
                    z-10
                    flex
                    items-center
                    gap-3
                    sm:gap-4
                  "
                >
                  {/* Button Text */}
                  <span className="text-sm sm:text-base">
                    <WavyText text="Start Your Project" />
                  </span>

                  {/* Arrow */}
                  <span
                    className="
                      flex
                      h-9
                      w-9
                      items-center
                      justify-center
                      rounded-full
                      transition-all
                      duration-500
                      group-hover:translate-x-1
                      group-hover:rotate-45
                      sm:h-10
                      sm:w-10
                    "
                    style={{
                      backgroundColor: "var(--bg-primary)",
                      color: "var(--text-primary)",
                    }}
                  >
                    <ArrowUpRight size={18} />
                  </span>
                </span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BlogCTA;